// libraries
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Button, Input, Switch } from 'antd';
import { Divider, notification, Table, Tooltip, Typography } from 'antd';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';

// resources
import * as SproglandService from '../../../../services/bb-api/red-cross/sprogland';
import { RemoveAccessModal } from '../remove-access-modal';
import { CreateVolunteerModal } from '../create-volunteer-modal';
import { SharedTable } from '../../../shared/table';
import { formatDate } from '../../../../utils/date';
import { txt } from '../../../../locales/i18n';

export function SproglandVolunteersTab() {
	const [volunteers, setVolunteers] = useState<Record<string, any>[]>([]);
	const [matches, setMatches] = useState<number>(0);
	const [loading, setLoading] = useState<boolean>(true);
	const [search, setSearch] = useState<string>('');
	const [showModal, setShowModal] = useState<boolean>(false);

	const searchTimeout = useRef<NodeJS.Timeout | null>(null);

	const queryVolunteers = useCallback(async (search) => {
		setLoading(true);

		try {
			const { records, matches } = await SproglandService.queryVolunteers({ search });
			setVolunteers(records);
			setMatches(matches);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
		}

		setLoading(false);
	}, []);

	useEffect(() => {
		if (searchTimeout.current !== null) {
			clearTimeout(searchTimeout.current);
		}

		if (search === '*') {
			setSearch('');
		}

		searchTimeout.current = setTimeout(async () => queryVolunteers(search), 500);
	}, [search, queryVolunteers]);

	function renderNumber(number: number): number | string {
		return number || '-';
	}

	function renderPhoneNumber({ phone, phonePrefix }: Record<string, any>): string | null {
		if (phone === null) return '-';
		if (phonePrefix === null) return phone;
		return `+${phonePrefix} ${phone}`;
	}

	async function handleCreateVolunteer(values: any): Promise<void> {
		try {
			await SproglandService.createVolunteer(values);
			setSearch('*');
		} catch (error) {
			throw error;
		}
	}

	async function handleLinkVolunteer(userId: number, juniorAccess: boolean): Promise<void> {
		try {
			await SproglandService.linkVolunteer(userId, juniorAccess);
			notification.success({ message: txt('notification.create successful') });
			setSearch('*');
		} catch (error) {
			const errorMessage: string = (error as Error).message;

			if (errorMessage === 'EMAIL_ALREADY_TAKEN') {
				notification.error({ message: txt('notification.email occupied') });
				return;
			}

			notification.error({ message: txt('notification.create failed') });
		}
	}

	function renderEmail(email: string) {
		return <Typography.Link href={`mailto:${email}`}>{email}</Typography.Link>;
	}

	return (
		<StyledSproglandVolunteersTab>
			<section style={{ display: 'flex', alignItems: 'center', paddingInline: '2ch' }}>
				<Tooltip title={txt('global.create')}>
					<Button type="primary" onClick={() => setShowModal(true)}>
						<PlusOutlined />
					</Button>
				</Tooltip>

				<Divider type="vertical" />

				<Tooltip title="Email/ Navn">
					<Input
						style={{ width: 'auto' }}
						placeholder="Email/ Navn"
						width="32ch"
						value={search}
						onChange={({ target }) => setSearch(target.value)}
						allowClear
					/>
				</Tooltip>

				<Divider type="vertical" />

				<Typography.Text type="secondary">
					{loading ? <LoadingOutlined /> : txt('global.table query notification', { amount: matches })}
				</Typography.Text>
			</section>

			<SharedTable rowKey="id" dataSource={volunteers} loading={loading} cursor="default">
				<Table.Column dataIndex="email" title="Email" width="32ch" ellipsis render={renderEmail} />
				<Table.Column dataIndex="username" title={txt('global.username')} ellipsis />
				<Table.Column dataIndex="age" title={txt('global.age')} width="10ch" />
				<Table.Column dataIndex="municipality" title={txt('global.municipality')} ellipsis />
				<Table.Column title={txt('global.phone')} width="18ch" render={renderPhoneNumber} />

				<Table.Column dataIndex="created" title={txt('global.created')} render={formatDate} ellipsis />

				<Table.Column
					title="Junior"
					render={(record: Record<string, any>) => (
						<JuniorAccessCell juniorAccess={record.juniorAccess} userId={record.id} />
					)}
					width="12ch"
					align="center"
				/>

				<Table.Column
					title="Sessions"
					dataIndex="sessionCount"
					render={renderNumber}
					width="12ch"
					align="center"
				/>

				<Table.Column
					title="Junior sessions"
					dataIndex="juniorSessionCount"
					render={renderNumber}
					width="12ch"
					align="center"
				/>

				<Table.Column
					title={txt('global.access')}
					dataIndex="id"
					render={(userId: number) => <AccessCell userId={userId} resetSearch={() => setSearch('*')} />}
					width="28ch"
				/>
			</SharedTable>

			<CreateVolunteerModal
				getUsersCallback={SproglandService.queryUsers}
				visible={showModal}
				createVolunteer={handleCreateVolunteer}
				linkVolunteer={handleLinkVolunteer}
				onCancel={() => setShowModal(false)}
				showJuniorSwitch
			/>
		</StyledSproglandVolunteersTab>
	);
}

const StyledSproglandVolunteersTab = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1ch;
	height: 100%;
`;

function JuniorAccessCell(props: any) {
	const [checked, setChecked] = useState<boolean>(props.juniorAccess);

	async function toggleJuniorAccess(access: boolean) {
		try {
			setChecked(access);
			await SproglandService.toggleJuniorAccess(props.userId, access);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			setChecked(!access);
			notification.error({ message: txt('notification.update failed') });
		}
	}

	return <Switch checked={checked} onChange={toggleJuniorAccess} />;
}

function AccessCell({ userId, resetSearch }: { userId: number; resetSearch: () => void }) {
	const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

	async function removeAccess() {
		try {
			await SproglandService.removeAccess(userId);
			setShowDeleteModal(false);
			resetSearch();
			notification.success({ message: txt('notification.deletion successful') });
		} catch (error) {
			notification.error({ message: txt('notification.deletion failed') });
		}
	}

	return (
		<>
			<Button danger onClick={() => setShowDeleteModal(true)}>
				{txt('global.remove access')}
			</Button>

			<RemoveAccessModal
				service="Sprogland"
				visible={showDeleteModal}
				onOk={removeAccess}
				onCancel={() => setShowDeleteModal(false)}
			/>
		</>
	);
}
